/**
 * Live refresh plumbing for the Modules (Module 360) area — a THIN SELECTOR
 * over the shared realtime bus (`src/realtime/`).
 *
 * The API's Knex services emit standard Feathers CRUD events, and channels.ts
 * publishes these four to the `internal` staff channel (STAFF_BROADCAST_CRUD_
 * PATHS). `realtime/channels.ts`'s `moduleCrudChannel` is the single listener
 * set for them; ModulesStore patches its catalog rows in place and
 * ModuleDetailStore registers a handler scoped to the open governedSlug.
 *
 * The sync job counts as a writer: `yarn catalog:sync` writes `module_catalog`,
 * `module_governance` and `module_releases` THROUGH their services, so a sync
 * run streams into an open page exactly like a human edit would.
 *
 * WHAT THIS FILE USED TO BE (until #3820): the last hand-rolled socket owner
 * the realtime bus was built to remove — its own `io()` connection, opened per
 * visit with a caller-supplied JWT and closed when the last subscriber left.
 * It heard nothing: none of the four services was on the API's staff
 * broadcast, so every event was published to nobody. And every Module 360
 * visit opened one more socket against the API's per-address connect-rate
 * window, which every other tab behind the same address shares.
 *
 * CONNECTION: none is opened here — see `realtime/realtimeBus.ts`.
 * AUTH: sourced internally by the bus from the AuthStore; no token parameter.
 * IF THE STREAM IS DOWN: the bus records a loud diagnostic and the views stay
 * pull-only (header Refresh / post-action re-pulls).
 */
import type RootStore from "stores/RootStore";
import {
  MODULE_REALTIME_SERVICES,
  moduleCrudChannel,
  subscribeRealtime,
  type CrudAction,
  type ModuleRealtimeService,
  type ServiceCrudEvent,
} from "realtime";

export { MODULE_REALTIME_SERVICES };
export type { ModuleRealtimeService };

/** Historical alias for the CRUD action union. */
export type ModuleRealtimeAction = CrudAction;

/** One CRUD event off any of the four module-scoped services. */
export type ModuleServiceEvent = ServiceCrudEvent<ModuleRealtimeService>;

type ModuleEventHandler = (event: ModuleServiceEvent) => void;

/**
 * Subscribe to live module-area CRUD events (all four services). Returns an
 * idempotent disposer; the bus tears the listeners down when the last
 * subscriber across the app leaves (route onExit).
 *
 * The handler comes first, as it always has (the stores' test doubles capture
 * it by position); the root store is what the bus resolves the shared
 * connection and the JWT from. The caller cannot supply a token.
 */
export function subscribeModuleEvents(
  handler: ModuleEventHandler,
  rootStore: RootStore,
): () => void {
  return subscribeRealtime(rootStore, moduleCrudChannel, handler);
}
